import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MEETING_CALENDAR_STATUS_LABEL,
  MEETING_CALENDAR_STATUSES,
  meetingCalendarStatus,
  type MeetingCalendarStatus,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { PatientLabel } from "@/components/PatientLabel";
import { MeetingStatusTag } from "@/components/meeting-calendar/MeetingStatusTag";
import { PASSED_MEETING_NOTE } from "@/lib/meeting-labels";
import { meetingKindLabel } from "@/lib/meeting-calendar";
import { patientMeetingHref } from "@/lib/patient-links";
import { useRefreshOnFocus } from "@/lib/use-refresh-on-focus";
import { useReadOnly, useViewPath } from "@/components/SupportView";
import {
  cancelClinicianMeeting,
  fetchClinicianMeetings,
  fetchMeetingCalendar,
  UnauthorizedError,
  type ClinicianMeeting,
} from "@/lib/clinician-api";

// TES-1233 — the list under "Möten". Emelie: "Listvyn får finnas kvar för
// sökning, filtrering och historik, men ska inte vara standard." The calendar
// is the default (Meetings.tsx); this view adds what the calendar cannot do:
// search by patient, filter by status, and the last six weeks of history.

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// The history the list can show: the calendar endpoint's widest range.
const HISTORY_DAYS = 42;

function matches(m: ClinicianMeeting, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    (m.patient.name ?? "").toLowerCase().includes(q) ||
    m.patient.personalNumberLast6.includes(q)
  );
}

export function MeetingsList() {
  const { signOut } = useAuth();
  // TES-1040 — the admin's read-only support view: no cancelling.
  const readOnly = useReadOnly();
  const viewPath = useViewPath();
  const [meetings, setMeetings] = useState<ClinicianMeeting[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // TES-291 — "today" filter so the doctor can focus on the day's consults.
  const [todayOnly, setTodayOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<MeetingCalendarStatus | "">(
    "",
  );
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<ClinicianMeeting[] | null>(null);

  const keep = useCallback(
    (m: ClinicianMeeting) =>
      matches(m, query) &&
      (!statusFilter || meetingCalendarStatus(m) === statusFilter),
    [query, statusFilter],
  );

  const visible = useMemo(
    () =>
      meetings
        ? todayOnly
          ? meetings.filter((m) => isToday(m.scheduledAt))
          : meetings
        : null,
    [meetings, todayOnly],
  );

  // Past, undocumented meetings: the doctor's main task after a meeting is the
  // documentation — they are listed in their own section at the top.
  const needsDocumentation = useMemo(
    () =>
      (visible ?? []).filter(
        (m) =>
          new Date(m.scheduledAt).getTime() < Date.now() - 60 * 60 * 1000 &&
          m.workflowStatus !== "documented",
      ),
    [visible],
  );
  const upcoming = useMemo(
    () =>
      (visible ?? [])
        .filter((m) => !needsDocumentation.includes(m))
        .filter(keep),
    [visible, needsDocumentation, keep],
  );
  const historyRows = useMemo(
    () =>
      (history ?? [])
        .filter((m) => new Date(m.scheduledAt).getTime() < Date.now())
        .filter(keep)
        .sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt)),
    [history, keep],
  );

  const load = useCallback(() => {
    fetchClinicianMeetings()
      .then((r) => setMeetings(r.meetings))
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [signOut]);

  useEffect(load, [load]);
  // TES-1383 — a meeting the patient cancels or moves leaves the list when
  // the doctor comes back to the tab, not only on a reload.
  useRefreshOnFocus(load);

  useEffect(() => {
    if (!showHistory || history) return;
    const now = new Date();
    fetchMeetingCalendar(
      new Date(now.getTime() - HISTORY_DAYS * 86_400_000),
      now,
    )
      .then((r) => setHistory(r.meetings))
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [showHistory, history, signOut]);

  async function cancel(meeting: ClinicianMeeting) {
    if (
      !window.confirm(
        `Avboka mötet ${fmt(meeting.scheduledAt)}? Patienten meddelas och kan boka ny tid.`,
      )
    ) {
      return;
    }
    setBusyId(meeting.id);
    setError(null);
    try {
      await cancelClinicianMeeting(meeting.id);
      load();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-wrap items-end gap-md">
        <label className="flex flex-col gap-xs text-sm">
          <span className="text-textSecondary">Sök patient</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Namn eller personnummer"
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs"
          />
        </label>
        <label className="flex flex-col gap-xs text-sm">
          <span className="text-textSecondary">Status</span>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as MeetingCalendarStatus | "")
            }
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs"
          >
            <option value="">Alla</option>
            {MEETING_CALENDAR_STATUSES.map((s) => (
              <option key={s} value={s}>
                {MEETING_CALENDAR_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          aria-pressed={todayOnly}
          onClick={() => setTodayOnly((v) => !v)}
          className={`px-md py-xs rounded-md border text-sm ${
            todayOnly
              ? "bg-brand text-textOnBrand border-brand"
              : "border-surfaceMuted text-textSecondary hover:text-textPrimary"
          }`}
        >
          Idag
        </button>
        <label className="flex items-center gap-xs text-sm">
          <input
            type="checkbox"
            checked={showHistory}
            onChange={(e) => setShowHistory(e.target.checked)}
          />
          Visa historik (sex veckor)
        </label>
        {/* Sitting next to the "Idag" toggle, a bare "16 bokade" read as
            "16 booked today" even with the filter off — and the number also
            counts the past, undocumented meetings listed below. Say which
            set is being counted. */}
        <span className="text-sm text-textSecondary">
          {visible
            ? `${visible.length} möten ${todayOnly ? "idag" : "totalt"}`
            : "Hämtar…"}
        </span>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {needsDocumentation.length > 0 ? (
        <div className="bg-surface border border-brand/40 rounded-md p-md flex flex-col gap-sm">
          <h2 className="font-display text-lg">Att dokumentera</h2>
          {/* TES-1183 — see Today.tsx. */}
          <p className="text-sm text-textSecondary">
            Genomfört – dokumentation saknas. {PASSED_MEETING_NOTE}
          </p>
          {needsDocumentation.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between gap-md border-t border-surfaceMuted pt-sm"
            >
              <div className="flex flex-wrap items-center gap-x-sm gap-y-xs text-sm">
                <span className="font-medium">{fmt(m.scheduledAt)}</span>
                <PatientLabel
                  patientId={m.patient.id}
                  name={m.patient.name}
                  personalNumberLast6={m.patient.personalNumberLast6}
                  link
                />
                <span className="text-textSecondary">
                  {meetingKindLabel(m)}
                </span>
                <MeetingStatusTag status={meetingCalendarStatus(m)} />
              </div>
              <Link
                to={viewPath(patientMeetingHref(m.patient.id, m.id))}
                className="px-md py-xs rounded-md bg-brand text-textOnBrand text-sm hover:opacity-90"
              >
                {readOnly ? "Öppna mötet" : "Dokumentera"}
              </Link>
            </div>
          ))}
        </div>
      ) : null}

      {visible && visible.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          {todayOnly ? "Inga möten idag." : "Inga schemalagda möten just nu."}
        </div>
      ) : null}

      {upcoming.length > 0 ? (
        <MeetingTable
          caption="Kommande möten"
          rows={upcoming}
          renderActions={(m) => (
            <>
              {/* TES-506 — a meeting opens on ONE screen. "Anslut" sat
                  here and opened the video as a full-screen overlay on
                  top of the list, so the doctor entered the call without
                  the patient's details and without the documentation
                  form. The "Anslut" button now lives on the meeting
                  screen, next to the material she needs during the
                  call. */}
              {readOnly || meetingCalendarStatus(m) !== "booked" ? null : (
                <button
                  type="button"
                  onClick={() => cancel(m)}
                  disabled={busyId === m.id}
                  className="px-md py-xs rounded-md border border-surfaceMuted text-textSecondary text-sm hover:text-danger hover:border-danger disabled:opacity-60"
                >
                  Avboka
                </button>
              )}
              <Link
                to={viewPath(patientMeetingHref(m.patient.id, m.id))}
                className="px-md py-xs rounded-md bg-brand text-textOnBrand text-sm hover:opacity-90"
              >
                Öppna mötet
              </Link>
            </>
          )}
        />
      ) : null}

      {showHistory ? (
        history === null ? (
          <p className="text-sm text-textSecondary">Hämtar historik…</p>
        ) : historyRows.length === 0 ? (
          <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
            Inga tidigare möten de senaste sex veckorna.
          </div>
        ) : (
          <MeetingTable
            caption="Historik"
            rows={historyRows}
            renderActions={(m) => (
              <Link
                to={viewPath(patientMeetingHref(m.patient.id, m.id))}
                className="px-md py-xs rounded-md border border-surfaceMuted text-sm hover:border-brand"
              >
                Öppna mötet
              </Link>
            )}
          />
        )
      ) : null}
    </div>
  );
}

function MeetingTable({
  caption,
  rows,
  renderActions,
}: {
  caption: string;
  rows: ClinicianMeeting[];
  renderActions: (m: ClinicianMeeting) => React.ReactNode;
}) {
  return (
    <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
      <table className="w-full min-w-[36rem] text-sm">
        <caption className="px-md pt-sm text-left font-display text-lg">
          {caption}
        </caption>
        <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
          <tr>
            <th className="text-left px-md py-sm font-medium">Tid</th>
            <th className="text-left px-md py-sm font-medium">Patient</th>
            <th className="text-left px-md py-sm font-medium">Mötestyp</th>
            <th className="text-left px-md py-sm font-medium">Status</th>
            <th className="px-md py-sm">
              <span className="sr-only">Åtgärder</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr
              key={m.id}
              className="border-t border-surfaceMuted hover:bg-surfaceMuted/40"
            >
              <td className="px-md py-sm">{fmt(m.scheduledAt)}</td>
              <td className="px-md py-sm">
                <PatientLabel
                  patientId={m.patient.id}
                  name={m.patient.name}
                  personalNumberLast6={m.patient.personalNumberLast6}
                  link
                />
              </td>
              <td className="px-md py-sm">{meetingKindLabel(m)}</td>
              <td className="px-md py-sm">
                <MeetingStatusTag status={meetingCalendarStatus(m)} />
              </td>
              <td className="px-md py-sm text-right">
                <div className="flex justify-end gap-xs">
                  {renderActions(m)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
